// @ts-nocheck
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  Calendar,
  FileText,
  Gavel,
  List,
  ListTodo,
  Loader2,
  Mail,
  MessageSquare,
  Sparkles,
} from 'lucide-react';
import { supabase } from '@saas-boilerplate/shared';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/components/ui/utils';

const WorkspaceSearchOpenerContext = createContext<() => void>(() => {});

export function useWorkspaceSearchOpener() {
  return useContext(WorkspaceSearchOpenerContext);
}

const HIT_LABEL: Record<string, string> = {
  note: 'Notes',
  decision: 'Decision',
  action: 'Action item',
  agenda: 'Agenda',
  check_in: 'Check-in',
  attendee: 'Attendee',
  series: 'Meeting series',
  transcript: 'Transcript',
  transcript_summary: 'Transcript summary',
};

function mapHitKindToSessionTab(hitKind: string) {
  const kind = (hitKind || '').toLowerCase();
  switch (kind) {
    case 'action':
    case 'action_item':
    case 'action_items':
      return 'actions';
    case 'decision':
    case 'decisions':
      return 'decisions';
    case 'check_in':
    case 'check-in':
    case 'checkin':
    case 'checkins':
      return 'checkins';
    case 'attendee':
    case 'attendees':
      return 'people';
    case 'note':
    case 'notes':
    case 'meeting_note':
    case 'meeting_notes':
    case 'summary':
      return 'summary';
    case 'agenda':
    case 'agendas':
      return 'agenda';
    case 'transcript':
    case 'transcript_summary':
      return 'transcript';
    default:
      return 'agenda';
  }
}

type SessionTabTarget = 'agenda' | 'checkins' | 'summary' | 'actions' | 'decisions' | 'people' | 'transcript';

function HitIcon({ kind }: { kind: string }) {
  const c = 'h-4 w-4 shrink-0 text-muted-foreground';
  switch (kind) {
    case 'note':
      return <FileText className={c} strokeWidth={1.75} />;
    case 'decision':
      return <Gavel className={c} strokeWidth={1.75} />;
    case 'action':
      return <ListTodo className={c} strokeWidth={1.75} />;
    case 'agenda':
      return <List className={c} strokeWidth={1.75} />;
    case 'check_in':
      return <MessageSquare className={c} strokeWidth={1.75} />;
    case 'attendee':
      return <Mail className={c} strokeWidth={1.75} />;
    case 'series':
      return <Sparkles className={c} strokeWidth={1.75} />;
    case 'transcript':
    case 'transcript_summary':
      return <FileText className={c} strokeWidth={1.75} />;
    default:
      return <Calendar className={c} strokeWidth={1.75} />;
  }
}

function highlightSnippet(text: string, query: string): ReactNode {
  const q = query.trim();
  if (!q || !text) return text;
  const lower = text.toLowerCase();
  const idx = lower.indexOf(q.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="rounded bg-amber-200/90 px-0.5 text-foreground dark:bg-amber-900/60">
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>
  );
}

function formatSessionWhen(iso: string | null): string {
  if (!iso) return 'Unscheduled session';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Session';
  return d.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function groupSearchRows(rows: any[]) {
  const map = new Map<
    string,
    {
      meetingId: string;
      seriesId: string;
      seriesTitle: string;
      meetingStartsAt: string | null;
      hits: any[];
    }
  >();
  for (const r of rows) {
    const key = `${r.series_id}::${r.meeting_id}`;
    let g = map.get(key);
    if (!g) {
      g = {
        meetingId: r.meeting_id,
        seriesId: r.series_id,
        seriesTitle: r.series_title,
        meetingStartsAt: r.meeting_starts_at,
        hits: [],
      };
      map.set(key, g);
    }
    const dedupeKey = `${r.hit_kind}::${(r.snippet || '').trim().toLowerCase()}`;
    const already = g.hits.some(
      (x) => `${x.hit_kind}::${(x.snippet || '').trim().toLowerCase()}` === dedupeKey,
    );
    if (!already) {
      g.hits.push(r);
    }
  }
  return [...map.values()].sort((a, b) => {
    const ta = a.meetingStartsAt ? new Date(a.meetingStartsAt).getTime() : 0;
    const tb = b.meetingStartsAt ? new Date(b.meetingStartsAt).getTime() : 0;
    if (tb !== ta) return tb - ta;
    const c = a.seriesTitle.localeCompare(b.seriesTitle);
    if (c !== 0) return c;
    return tb - ta;
  });
}

function WorkspaceSearchDialog({
  open,
  onOpenChange,
  workspaceId,
  workspaceName,
  userDisplayName,
  onNavigateToMeeting,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  workspaceId: string | null | undefined;
  workspaceName: string | null | undefined;
  userDisplayName: string | null | undefined;
  onNavigateToMeeting: (meetingId: string, preferredTab?: SessionTabTarget) => void;
}) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  /** Last usage-event log, to avoid logging every keystroke-refinement as a separate search. */
  const lastSearchLog = useRef<{ q: string; at: number }>({ q: '', at: 0 });

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(query.trim()), 280);
    return () => window.clearTimeout(t);
  }, [query]);

  /**
   * Usage telemetry for the staff admin dashboard (usage_events, migration 0108): count a "search"
   * once per distinct query, not per keystroke - skip when the new query is just a refinement
   * (prefix-extension either way) of one logged in the last 8s. Fire-and-forget; never blocks UI.
   */
  const logSearchEvent = useCallback(
    (q: string) => {
      if (!workspaceId) return;
      const prev = lastSearchLog.current;
      const now = Date.now();
      const isRefinement =
        now - prev.at < 8000 && (q.startsWith(prev.q) || prev.q.startsWith(q)) && prev.q.length > 0;
      if (isRefinement) {
        lastSearchLog.current = { q, at: prev.at };
        return;
      }
      lastSearchLog.current = { q, at: now };
      void (async () => {
        try {
          const { data } = await supabase.auth.getSession();
          const uid = data.session?.user?.id;
          if (!uid) return;
          await supabase.from('usage_events').insert({
            workspace_id: workspaceId,
            user_id: uid,
            event_type: 'workspace_search',
            metadata: { query_length: q.length },
          });
        } catch {
          /* telemetry only - never surface */
        }
      })();
    },
    [workspaceId],
  );

  useEffect(() => {
    if (!open) {
      setQuery('');
      setDebounced('');
      setRows([]);
      return;
    }
    let cancelled = false;
    (async () => {
      if (!workspaceId || debounced.length < 2) {
        setRows([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      setRows([]);
      logSearchEvent(debounced);
      const { data, error } = await supabase.rpc('search_workspace_meeting_hits', {
        p_workspace_id: workspaceId,
        p_query: debounced,
      });
      if (cancelled) return;
      if (error) {
        setRows([]);
      } else {
        setRows(data || []);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, workspaceId, debounced]);

  const grouped = useMemo(() => groupSearchRows(rows), [rows]);

  const placeholder = `Search meetings in ${workspaceName?.trim() || 'your workspace'}…`;

  const pick = useCallback(
    (meetingId: string, hitKind?: string) => {
      onNavigateToMeeting(meetingId, mapHitKindToSessionTab(hitKind || ''));
      onOpenChange(false);
      setQuery('');
    },
    [onNavigateToMeeting, onOpenChange],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          '!flex !flex-col',
          'gap-0 overflow-hidden p-0',
          'h-[min(760px,calc(100dvh-2rem))] max-h-[calc(100dvh-2rem)]',
          'max-w-[calc(100%-2rem)] sm:max-w-3xl',
          'border border-border/60 bg-background shadow-2xl',
        )}
      >
        <DialogTitle className="sr-only">Search meetings</DialogTitle>
        <Command shouldFilter={false} className="flex min-h-0 flex-1 flex-col rounded-lg" loop>
          <div className="relative">
            <CommandInput
              placeholder={placeholder}
              value={query}
              onValueChange={setQuery}
              autoFocus
              className="h-12 border-0 py-3 pr-10 shadow-none outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:ring-0"
            />
            {loading ? (
              <Loader2
                className="pointer-events-none absolute right-10 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground"
                aria-hidden
              />
            ) : null}
          </div>
          <CommandList className="!max-h-none !h-auto min-h-0 flex-1 overflow-y-auto py-2">
            {debounced.length === 0 ? (
              <div className="px-4 py-10 text-sm text-muted-foreground">
                Search across notes, decisions, action items, agenda, check-ins, attendees, and meeting titles.
              </div>
            ) : null}
            {debounced.length > 0 && debounced.length < 2 ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                Type at least 2 characters to search notes, decisions, actions, check-ins, and more.
              </div>
            ) : null}
            {debounced.length >= 2 && !loading && rows.length === 0 ? (
              <CommandEmpty className="py-10 text-muted-foreground">
                No matches in meetings yet. Transcripts will be included here when available.
              </CommandEmpty>
            ) : null}
            {grouped.map((g) => (
              <CommandGroup
                key={`${g.seriesId}-${g.meetingId}`}
                heading={`${g.seriesTitle} • ${formatSessionWhen(g.meetingStartsAt)}`}
                className={cn(
                  'mx-2 my-2 rounded-lg border border-border/50 bg-muted/20 px-1 py-1',
                  '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1',
                  '[&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-muted-foreground',
                )}
              >
                {g.hits.slice(0, 6).map((h, i) => (
                  <CommandItem
                    key={`${g.meetingId}-${h.hit_kind}-${i}-${h.snippet?.slice(0, 12)}`}
                    value={`${g.meetingId}-${h.hit_kind}-${i}`}
                    className="flex cursor-pointer items-start gap-3 rounded-md px-3 py-2.5 aria-selected:bg-muted"
                    onSelect={() => pick(g.meetingId, h.hit_kind)}
                  >
                    <HitIcon kind={h.hit_kind} />
                    <div className="min-w-0 flex-1 text-left">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {HIT_LABEL[h.hit_kind] || h.hit_kind}
                      </p>
                      <p className="mt-0.5 line-clamp-2 text-sm leading-snug text-foreground">
                        {highlightSnippet(h.snippet || '', debounced)}
                      </p>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border/50 px-3 py-2 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <kbd className="rounded border border-border bg-muted px-1 font-mono">↑</kbd>
              <kbd className="rounded border border-border bg-muted px-1 font-mono">↓</kbd>
              <span>to navigate</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <kbd className="rounded border border-border bg-muted px-1 font-mono">↵</kbd>
              <span>to open session</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <kbd className="rounded border border-border bg-muted px-1 font-mono">esc</kbd>
              <span>to close</span>
            </span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

export function WorkspaceSearchProvider({
  children,
  workspaceId,
  workspaceName,
  userDisplayName,
  onNavigateToMeeting,
}: {
  children: ReactNode;
  workspaceId: string | null | undefined;
  workspaceName: string | null | undefined;
  userDisplayName: string | null | undefined;
  onNavigateToMeeting: (meetingId: string, preferredTab?: SessionTabTarget) => void;
}) {
  const [open, setOpen] = useState(false);
  const openSearch = useCallback(() => setOpen(true), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <WorkspaceSearchOpenerContext.Provider value={openSearch}>
      {children}
      <WorkspaceSearchDialog
        open={open}
        onOpenChange={setOpen}
        workspaceId={workspaceId}
        workspaceName={workspaceName}
        userDisplayName={userDisplayName}
        onNavigateToMeeting={onNavigateToMeeting}
      />
    </WorkspaceSearchOpenerContext.Provider>
  );
}
